/**
 * QueueTriageRail — the pre-selection state of the accountant WorkDesk's right rail.
 *
 * Replaces the bare "Select a Case" placeholder (the state every session starts in)
 * with a triage of WHERE the work is, derived client-side from the already-fetched
 * available-queue rows. Each row routes the accountant straight to that case.
 *
 * Pure presentation: the parent owns the queue data and the selection.
 */

import React, { useMemo } from 'react';
import { ChevronRight, CheckCircle } from 'lucide-react';
import { FinancialValue } from '../shared/FinancialValue';

interface QueueTriageRailProps {
  cases: any[];
  onSelectCase: (id: string) => void;
}

export default function QueueTriageRail({ cases, onSelectCase }: QueueTriageRailProps) {
  const { oldest, blockerTotal, casesWithBlockers, worst, largest, largestKicker } = useMemo(() => {
    if (cases.length === 0) {
      return {
        oldest: null,
        blockerTotal: 0,
        casesWithBlockers: 0,
        worst: null,
        largest: null,
        largestKicker: 'LARGEST UNCLAIMED',
      };
    }

    const oldest = cases.reduce((max, c) => {
      if (!max) return c;
      if ((c.days_stale || 0) > (max.days_stale || 0)) return c;
      if ((c.days_stale || 0) === (max.days_stale || 0) && (c.estimated_value || 0) > (max.estimated_value || 0)) return c;
      return max;
    }, null);

    const blockerTotal = cases.reduce((sum, c) => sum + (c.readiness_issues?.length || 0), 0);
    const casesWithBlockers = cases.filter((c) => (c.readiness_issues?.length || 0) > 0).length;

    const worst = cases.reduce((max, c) => {
      if (!max) return c;
      if ((c.readiness_issues?.length || 0) > (max.readiness_issues?.length || 0)) return c;
      return max;
    }, null);

    const unclaimed = cases.filter((c) => !c.is_claimed);
    let largest = null;
    let largestKicker = 'LARGEST UNCLAIMED';

    if (unclaimed.length > 0) {
      largest = unclaimed.reduce((max, c) => {
        if (!max) return c;
        if ((c.estimated_value || 0) > (max.estimated_value || 0)) return c;
        return max;
      }, null);
    } else {
      largest = cases.reduce((max, c) => {
        if (!max) return c;
        if ((c.estimated_value || 0) > (max.estimated_value || 0)) return c;
        return max;
      }, null);
      largestKicker = 'LARGEST IN QUEUE';
    }

    return { oldest, blockerTotal, casesWithBlockers, worst, largest, largestKicker };
  }, [cases]);

  if (cases.length === 0) {
    return (
      <div className="rounded-xl bg-bg-surface border border-border p-4">
        <div className="text-[11px] uppercase tracking-wider text-text-dim font-medium mb-3">
          QUEUE TRIAGE
        </div>
        <div className="flex items-center gap-3 text-text-muted">
          <CheckCircle className="w-5 h-5" />
          <span>Queue is clear.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-bg-surface border border-border p-4">
      <div className="text-[11px] uppercase tracking-wider text-text-dim font-medium mb-3">
        QUEUE TRIAGE
      </div>

      <div className="divide-y divide-border-faint">
        <button
          type="button"
          onClick={() => oldest?.id && onSelectCase(oldest.id)}
          className="w-full text-left group focus-visible:ring-1 focus-visible:ring-brand focus-visible:ring-inset focus:outline-none py-3 first:pt-0"
        >
          <div className="font-mono text-[10px] uppercase text-text-dim mb-1">
            OLDEST IN QUEUE
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="text-[13px] text-text-primary truncate flex-1">
              {oldest?.business_name || 'Untitled case'}
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-text-secondary">{(oldest?.days_stale || 0)}d</span>
              <ChevronRight className="w-4 h-4 text-text-dim group-hover:text-brand" />
            </div>
          </div>
        </button>

        {blockerTotal === 0 ? (
          <div className="py-3">
            <div className="font-mono text-[10px] uppercase text-text-dim mb-1">
              QUEUE BLOCKERS
            </div>
            <div className="text-text-muted text-sm">Queue clear — no blockers</div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => worst?.id && onSelectCase(worst.id)}
            className="w-full text-left group focus-visible:ring-1 focus-visible:ring-brand focus-visible:ring-inset focus:outline-none py-3"
          >
            <div className="font-mono text-[10px] uppercase text-text-dim mb-1">
              QUEUE BLOCKERS
            </div>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-text-primary">
                  <span className="font-mono" style={{ color: 'var(--color-blocker)' }}>
                    {blockerTotal}
                  </span>
                  <span>
                    {' '}BLOCKER{blockerTotal !== 1 ? 'S' : ''} · {casesWithBlockers} CASE{casesWithBlockers !== 1 ? 'S' : ''}
                  </span>
                </div>
                <div className="text-text-dim text-xs truncate mt-0.5">
                  Worst: {worst?.business_name || 'Untitled case'}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-text-dim group-hover:text-brand flex-shrink-0 mt-0.5" />
            </div>
          </button>
        )}

        <button
          type="button"
          onClick={() => largest?.id && onSelectCase(largest.id)}
          className="w-full text-left group focus-visible:ring-1 focus-visible:ring-brand focus-visible:ring-inset focus:outline-none py-3 last:pb-0"
        >
          <div className="font-mono text-[10px] uppercase text-text-dim mb-1">
            {largestKicker}
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="text-[13px] text-text-primary truncate flex-1">
              {largest?.business_name || 'Untitled case'}
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-text-secondary">
                <FinancialValue value={largest?.estimated_value} />
              </span>
              <ChevronRight className="w-4 h-4 text-text-dim group-hover:text-brand" />
            </div>
          </div>
        </button>
      </div>

      <div className="text-xs text-text-dim mt-3 pt-3 border-t border-border-faint">
        Select a case for insights, benchmarks, and sign-off.
      </div>
    </div>
  );
}
